import { useState, type ReactNode } from 'react';
import {
  type ApiClient,
  type BenefitItem,
  type BenefitQueue,
  type CareRequest,
  type MedPassRow,
  type NutritionWorklist,
  type PartANoteRow,
  type Referral,
  type ReporterChecklistItem,
  type RosterResident,
  type WorklistCode,
  type MeasuresResponse,
  type BenefitReferenceRow,
} from '@aithora/shared';
import type { QueryState } from '../../useQuery';
import { useShownHonesty } from '../../session/DemoScope';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { A, AxView, CountCell, HonestyText, sx, type AxActions } from './parts';
import { AskAtlasBar, measuresAsk, medsAsk, requestsAsk } from './askAtlas';
import {
  NUT_DOC,
  NUT_PPD,
  kindOfBenefit,
  rateText,
  type Ax,
  type Lens,
  type MeasureGroup,
  type MeasureRow,
  type MedRow,
  type NoteRow,
  type NutView,
  type RefDraft,
  type RequestRow,
  WCHK as WCHK_T,
  wqFmt,
  dueFmt,
} from './model';
import { BuildLabel, BuildTagged, useBuildLabels } from '../../ui/buildLabels';

interface Common {
  now: number;
  ro: boolean;
  lens: Lens;
  writesOff: boolean;
  offReason: string | null;
  axActs: AxActions;
  busy: boolean;
  client: ApiClient;
  facilityId: string | null;
  act: (fn: () => Promise<unknown>) => Promise<boolean>;
  keep: (ax: Ax) => boolean;
  /** V7: the escalation ladder under a row (by row id), and the "Moving up the ladder" box. */
  ladder?: (id: string) => ReactNode;
  movingUp?: ReactNode;
  /** V9: Search & shortcuts is open (the code search, the source filters). */
  tools?: boolean;
  /** V9 "Pulled away": a moved item's tag ("From Jackie · Edna’s emergency"), by `tasks:<id>` or `medpass:<medication id>`. */
  coverTag?: (key: string) => string | null;
}

/** The tag a moved item carries in its own list (design x.from). */
function CoverTag({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <span style={sx('display:flex;gap:6px;flex-wrap:wrap;margin-top:2px;')}>
      <span data-testid="cover-tag" style={sx('display:inline-flex;align-items:center;padding:1px 8px;border-radius:6px;background:#F6F3EC;color:#3C454F;font-size:14px;font-weight:700;white-space:nowrap;line-height:1.35;')}>
        {text}
      </span>
    </span>
  );
}

/** The first answer is loading, or it failed: the tab's own skeleton or error. */
function Gate<T>({ q, children, rows = 5 }: { q: QueryState<T>; children: ReactNode; rows?: number }) {
  if (q.loading && q.data === null && !q.error) return <Skeleton rows={rows} />;
  if (q.error && q.data === null) return <ErrorState error={q.error} onRetry={q.reload} />;
  return (
    <>
      {children}
      {q.error && <ErrorState error={q.error} onRetry={q.reload} />}
    </>
  );
}

const cell = (k: string, v: string, foot: string, c: string, small?: boolean) => ({ k, v, foot, vColor: c, vSize: small ? '14px' : '22px' });

function Strip({ cells, mb = true }: { cells: ReturnType<typeof cell>[]; mb?: boolean }) {
  return (
    <div style={sx(`display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;min-width:0;${mb ? 'margin-bottom:6px;' : ''}`)}>
      {cells.map((t) => (
        <span key={t.k} title={t.foot} style={sx('display:flex;align-items:baseline;gap:6px;')}>
          <span data-wq-count={t.v} style={sx(`font-size:${t.vSize};font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:${t.vColor};`)}>{t.v}</span>
          <span style={sx('font-size:16px;color:#54606C;font-weight:600;')}>{t.k}</span>
        </span>
      ))}
    </div>
  );
}

const BTN34 = 'display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;cursor:pointer;';
// V5 worklist buttons (handoff 2026-10-05): two styles only, primary navy and a thin neutral outline.
const PRIMARY = 'background:#1D3F6B;color:#FFFFFF;';
const SECONDARY = 'background:#FCFAF4;border:1px solid rgba(34,39,46,.22);color:#22272E;';
const BTN44 = 'display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 14px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;cursor:pointer;';

// ── Measures (T1413-1546) ───────────────────────────────────────────────────

export function MeasuresTab(
  p: Common & {
    q: QueryState<MeasuresResponse>;
    rows: { row: MeasureRow; ax: Ax }[];
    allRows: { row: MeasureRow; ax: Ax }[];
    groups: MeasureGroup[];
    axById: Map<string, Ax>;
    mode: 'deadline' | 'resident';
    setMode: (m: 'deadline' | 'resident') => void;
    drafts: Record<string, string>;
    setDraft: (id: string, v: string) => void;
    capture: (id: string, value: string | number, text: string) => Promise<boolean>;
    codeQ: string;
    setCodeQ: (v: string) => void;
    scopeAll: boolean;
    setScopeAll: (v: boolean) => void;
    codesAll: { catalogSize: number; codes: WorklistCode[] } | null;
    hits: WorklistCode[];
    adding: boolean;
    setAdding: (v: boolean) => void;
    newItem: { what: string; res: string; owner: string; cad: 'once' | 'daily' | 'weekly' | 'monthly'; due: string; kind: 'task' | 'vitals'; at: string };
    setNewItem: (f: (x: { what: string; res: string; owner: string; cad: 'once' | 'daily' | 'weekly' | 'monthly'; due: string; kind: 'task' | 'vitals'; at: string }) => { what: string; res: string; owner: string; cad: 'once' | 'daily' | 'weekly' | 'monthly'; due: string; kind: 'task' | 'vitals'; at: string }) => void;
    residents: RosterResident[];
    ownerOpts: { key: string; label: string }[];
    addItem: (what: string, code: string | null) => Promise<boolean>;
  },
) {
  const { ro, lens, writesOff } = p;
  const buildLabels = useBuildLabels();
  const data = p.q.data;
  const all = p.allRows.map((r) => r.row);
  const overdue = all.filter((r) => r.ms < 0 && !r.isDone).length;
  const today = all.filter((r) => r.ms >= 0 && r.ms < 12 * 3_600_000 && !r.isDone).length;
  const doneN = data ? data.counts.done : 0;
  // The design's strip (wqStrip) counts only the row's own snoozes, which the action layer
  // replaced (wlLayer canExit false): a snooze from the action layer takes the row off the
  // list and into the shift handoff's SNOOZED group, not into this strip.
  const strip: [string, number, string][] = [
    ['overdue', overdue, overdue ? '#B3261E' : '#22272E'],
    ['due today', today, '#3C454F'],
    ['done this shift', doneN, '#1F6B52'],
  ];
  const scope = ro ? '' : lens === 'staff' ? 'Showing yours and unowned' : 'Everyone’s items · you can assign';
  const resume = p.rows.filter((r) => !r.row.isDone && String(p.drafts[r.row.id] ?? '').trim()).slice(0, 3);
  const codes = p.codesAll?.codes ?? [];
  const facilitySet = codes.filter((c) => c.performedNotCaptured);
  const pool = p.scopeAll ? codes : facilitySet;
  const poolN = p.scopeAll ? (p.codesAll?.catalogSize ?? codes.length) : facilitySet.length;
  const hits = p.codeQ.trim().length < 2 ? [] : p.hits.filter((h) => p.scopeAll || h.performedNotCaptured).slice(0, 8);
  const seg = (on: boolean, h = 36) => `display:inline-flex;align-items:center;min-height:${h}px;padding:0 12px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;` + (on ? 'background:#22272E;color:#FCFAF4;' : h === 36 ? 'color:#3C454F;' : 'color:#3C454F;background:#FCFAF4;');
  const inputOff = writesOff;
  const sel = 'flex:1 1 150px;min-width:0;height:44px;border-radius:10px;border:1px solid rgba(34,39,46,.18);background:#FFFFFF;font-family:inherit;font-size:16px;padding:0 8px;';

  return (
    <div data-screen-label="Worklist queue" style={sx('min-width:0;display:flex;flex-direction:column;flex:1 1 auto;min-height:0;gap:8px;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;min-width:0;')}>
        {strip.map(([k, v, c]) => (
          <CountCell key={k} v={String(v)} k={k} c={c} />
        ))}
        {data && (
          <HonestyText flags={[data]} figure="Worklist residents" hide={['MEASURED']} suffix=" residents" style="font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;" />
        )}
        <span style={sx('flex:1 1 10px;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>{scope}</span>
        <span role="tablist" aria-label="Group by" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.18);border-radius:10px;overflow:hidden;')}>
          {(
            [
              ['deadline', 'By deadline'],
              ['resident', 'By resident'],
            ] as ['deadline' | 'resident', string][]
          ).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={p.mode === k} className="wl-a" style={sx(seg(p.mode === k))} onClick={() => p.setMode(k)}>
              {label}
            </button>
          ))}
        </span>
      </div>
      {/* V9 "Summary first": the code search and + Add item sit behind Search & shortcuts. */}
      {!ro && p.tools && (
        <>
          <div style={sx('flex:0 0 auto;display:flex;gap:8px;flex-wrap:wrap;align-items:center;min-width:0;')}>
            <input
              type="text"
              value={p.codeQ}
              onChange={(e) => p.setCodeQ(e.target.value)}
              placeholder={`Other codes: search ${p.scopeAll ? 'all codes' : 'the facility set'}`}
              aria-label="Search codes"
              disabled={inputOff}
              style={sx('box-sizing:border-box;flex:1 1 220px;min-width:0;height:44px;padding:0 12px;border-radius:10px;border:1px solid rgba(34,39,46,.18);background:#FCFAF4;font-family:inherit;font-size:16px;color:#22272E;')}
            />
            <span role="tablist" aria-label="Code set" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.18);border-radius:10px;overflow:hidden;')}>
              {(
                [
                  [false, 'Facility set', facilitySet.length],
                  [true, 'All codes', p.codesAll?.catalogSize ?? codes.length],
                ] as [boolean, string, number][]
              ).map(([all, label, n]) => (
                <button key={label} type="button" role="tab" aria-selected={p.scopeAll === all} className="wl-a" style={sx(seg(p.scopeAll === all, 44))} onClick={() => p.setScopeAll(all)}>
                  {`${label} · ${n}`}
                </button>
              ))}
            </span>
            <span style={sx('flex:1 1 100%;order:3;font-size:14px;color:#54606C;')}>
              {`Searching ${poolN} code${poolN === 1 ? '' : 's'}, ${pool.filter((c) => c.rateStatus === 'VERIFIED').length} with verified rates${buildLabels ? (p.scopeAll ? ' · the full HCPCS set fills in when the extract runs' : ' · the facility set grows to about 300 when the loader runs') : ''}`}
            </span>
            <A style={BTN44 + 'border:1px solid rgba(47,92,151,.45);color:#264C7E;'} onClick={() => p.setAdding(!p.adding)} disabled={inputOff} title={p.offReason ?? undefined}>
              + Add item
            </A>
          </div>
          {hits.length > 0 && (
            <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;border:1px solid rgba(34,39,46,.14);border-radius:10px;background:#FCFAF4;max-height:180px;overflow-y:auto;')}>
              {hits.map((h) => (
                <button
                  key={h.code}
                  type="button"
                  className="wl-a wl-hover"
                  onClick={() => void p.addItem(h.shortDesc ?? h.code, h.code)}
                  disabled={inputOff}
                  style={sx('display:flex;align-items:center;gap:10px;min-height:44px;padding:0 12px;border-top:1px solid rgba(34,39,46,.07);text-decoration:none;color:#22272E;')}
                >
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;font-weight:600;')}>{h.shortDesc ?? h.code}</span>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                    {h.code} &middot; {rateText(h.rateCents, h.rateUnit) ?? 'no rate on file'}
                  </span>
                  <span style={sx('font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;color:' + (h.rateStatus === 'ESTIMATED' ? '#3C454F' : '#1F6B52') + ';')}>{h.rateStatus ?? ''}</span>
                </button>
              ))}
            </div>
          )}
          {p.adding && (
            <div style={sx('flex:0 0 auto;display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding:10px;border-radius:10px;background:rgba(47,92,151,.06);')}>
              <input
                type="text"
                value={p.newItem.what}
                onChange={(e) => p.setNewItem((x) => ({ ...x, what: e.target.value }))}
                placeholder="What needs doing"
                aria-label="What needs doing"
                style={sx('box-sizing:border-box;flex:2 1 220px;min-width:0;height:44px;padding:0 12px;border-radius:10px;border:1px solid rgba(34,39,46,.18);background:#FFFFFF;font-family:inherit;font-size:16px;')}
              />
              <select aria-label="Kind" value={p.newItem.kind} onChange={(e) => p.setNewItem((x) => ({ ...x, kind: e.target.value as 'task' | 'vitals' }))} style={sx(sel.replace('150px', '130px'))}>
                <option value="task">Care task</option>
                <option value="vitals">Vitals check</option>
              </select>
              <select aria-label="Resident" value={p.newItem.res} onChange={(e) => p.setNewItem((x) => ({ ...x, res: e.target.value }))} style={sx(sel)}>
                {p.residents.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name ?? 'Resident'}
                  </option>
                ))}
              </select>
              <select aria-label="Owner" value={p.newItem.owner} onChange={(e) => p.setNewItem((x) => ({ ...x, owner: e.target.value }))} style={sx(sel)}>
                {p.ownerOpts.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select aria-label="Cadence" value={p.newItem.cad} onChange={(e) => p.setNewItem((x) => ({ ...x, cad: e.target.value as 'once' }))} style={sx(sel.replace('150px', '130px'))}>
                {['once', 'daily', 'weekly', 'monthly'].map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
              <select aria-label="Due" value={p.newItem.due} onChange={(e) => p.setNewItem((x) => ({ ...x, due: e.target.value }))} style={sx(sel.replace('150px', '130px'))}>
                {['Today', 'Within 3 days', 'This week', 'This month'].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <input
                type="text"
                value={p.newItem.at}
                onChange={(e) => p.setNewItem((x) => ({ ...x, at: e.target.value.trim() }))}
                placeholder="At (08:00)"
                aria-label="At what time"
                style={sx('box-sizing:border-box;flex:0 0 96px;height:44px;padding:0 10px;border-radius:10px;border:1px solid rgba(34,39,46,.18);background:#FFFFFF;font-family:inherit;font-size:16px;')}
              />
              <A style={BTN44 + 'background:#2F5C97;color:#FFFFFF;'} onClick={() => p.newItem.what.trim() && void p.addItem(p.newItem.what.trim(), null)} disabled={inputOff || !p.newItem.what.trim()}>
                Add
              </A>
              <span style={sx('flex:1 1 100%;font-size:14px;color:#54606C;')}>A custom item carries no billing code unless you map one. It sorts with everything else. A vitals check with a time becomes a Record stop on the shift.</span>
            </div>
          )}
        </>
      )}
      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;min-width:0;padding-right:4px;')}>
        <Gate q={p.q} rows={6}>
          {p.allRows.length === 0 && data && <EmptyState compact title="Nothing on the worklist today" body="Items land here as they fall due in this building." />}
          <MeasureGroups {...p} />
        </Gate>
        {/* V9: tasks first; the ladder, Ask Atlas and the unsaved drafts sit under the list. */}
        <div style={sx('display:flex;flex-direction:column;gap:8px;margin-top:12px;min-width:0;')}>
          {p.movingUp}
          <AskAtlasBar
            label="Worklist ask Atlas"
            placeholder="Ask this list, or say “Hey Siri, ask Aithora what’s late”"
            {...measuresAsk(all, ro)}
            note="Atlas reads the list and fills drafts. It never marks care done or logs minutes; a person signs."
            onFill={ro ? undefined : p.setDraft}
          />
          {!ro && resume.length > 0 && (
            <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;padding:8px 12px;border-radius:10px;background:rgba(34,39,46,.10);')}>
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#3C454F;')}>PICK UP WHERE YOU LEFT OFF</span>
              {resume.map((r) => (
                <A
                  key={r.row.id}
                  style="display:inline-flex;align-items:center;min-height:44px;font-size:16px;font-weight:700;color:#22272E;text-decoration:none;"
                  onClick={() => {
                    const el = document.querySelector<HTMLElement>(`[data-wq-row="${r.row.id}"]`);
                    el?.scrollIntoView?.({ block: 'center' });
                    el?.querySelector('input')?.focus();
                  }}
                >
                  {`${r.row.res} · ${p.drafts[r.row.id]} ${r.row.ph} not saved →`}
                </A>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={sx('flex:0 0 auto;font-size:14px;color:#54606C;line-height:1.45;')}>{buildLabels ? 'The care comes first; the code follows it. A rate marked ESTIMATED is not yet verified against the fee schedule.' : 'The care comes first; the code follows it. A rate marked est. is not yet verified against the fee schedule.'}</div>
    </div>
  );
}

/** The measure rows, grouped by deadline or by resident (design wqGroups): the Measures tab, and Care Staff's own scheduled tasks. */
export function MeasureGroups(p: {
  groups: MeasureGroup[];
  axById: Map<string, Ax>;
  drafts: Record<string, string>;
  setDraft: (id: string, v: string) => void;
  capture: (id: string, value: string | number, text: string) => Promise<boolean>;
  ro: boolean;
  writesOff: boolean;
  offReason: string | null;
  axActs: AxActions;
  ladder?: (id: string) => ReactNode;
  coverTag?: (key: string) => string | null;
}) {
  const { ro, writesOff } = p;
  return (
    <>
      {p.groups.map((g) => (
        <div key={g.label} style={sx('margin-top:6px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;padding:6px 0;position:sticky;top:0;background:#F3EFE5;z-index:1;')}>
            <span style={sx(`width:8px;height:8px;border-radius:50%;background:${g.tone};`)} />
            <span style={sx(`font-size:14px;letter-spacing:.09em;text-transform:uppercase;font-weight:800;color:${g.tone};`)}>{g.label}</span>
            <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{g.n}</span>
          </div>
          {g.rows.map((r) => {
            const ax = p.axById.get(r.id)!;
            const val = p.drafts[r.id] ?? '';
            const live = !ro && !r.isDone;
            return (
              <div key={r.id} data-wq-row={r.id} style={sx(r.rowStyle)}>
                <span style={sx('flex:1 1 260px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                  <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                    <span style={sx('font-size:16px;font-weight:800;letter-spacing:-.01em;')}>{r.res}</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>{r.room}</span>
                    <span style={sx(r.trackStyle)}>{r.track}</span>
                  </span>
                  <span style={sx('font-size:16px;line-height:1.35;color:#22272E;')}>{r.task}</span>
                  <span style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
                    <span>{r.unlock}</span> <BuildTagged tag={r.badge}><span style={sx(r.badgeStyle)}>{r.badge}</span></BuildTagged>
                  </span>
                  <CoverTag text={p.coverTag?.(`tasks:${r.id}`) ?? null} />
                  {p.ladder ? p.ladder(r.id) : <span style={sx(`font-size:14px;font-weight:700;color:${r.ownerColor};`)}>{r.owner}</span>}
                </span>
                <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:6px;min-width:110px;')}>
                  <span data-wq-due={r.dueAt} style={sx(`font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:${r.dueColor};`)}>{r.dueText}</span>
                  {live && r.cap === 'number' && (
                    <span style={sx('display:flex;gap:6px;')}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={val}
                        onChange={(e) => p.setDraft(r.id, e.target.value)}
                        placeholder={r.ph}
                        aria-label={`${r.task}, ${r.res}`}
                        disabled={writesOff}
                        style={sx('box-sizing:border-box;width:76px;height:34px;padding:0 10px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;')}
                      />
                      <A style={BTN34 + PRIMARY} onClick={() => val.trim() && void p.capture(r.id, val.trim(), `Saved ${val.trim()} ${r.ph}`)} disabled={writesOff} title={p.offReason ?? undefined}>
                        Save
                      </A>
                    </span>
                  )}
                  {live && r.cap === 'yesno' && (
                    <span style={sx('display:flex;gap:6px;')}>
                      <A style={BTN34 + PRIMARY} onClick={() => void p.capture(r.id, 'yes', 'Done')} disabled={writesOff} title={p.offReason ?? undefined}>
                        Done
                      </A>
                      <A style={BTN34 + SECONDARY} onClick={() => void p.capture(r.id, 'no', 'Noted: not today')} disabled={writesOff} title={p.offReason ?? undefined}>
                        Not today
                      </A>
                    </span>
                  )}
                  {live && r.cap === 'score' && (
                    <span style={sx('display:flex;gap:4px;')}>
                      {[0, 1, 2, 3].map((v) => (
                        <A key={v} style={BTN34 + 'min-width:34px;padding:0;' + SECONDARY} onClick={() => void p.capture(r.id, v, `Scored ${v}`)} disabled={writesOff} label={`Score ${v}`}>
                          {String(v)}
                        </A>
                      ))}
                    </span>
                  )}
                  {r.isDone && <span style={sx('font-size:16px;font-weight:800;color:#1F6B52;')}>&#10003; {r.doneText}</span>}
                </span>
                <AxView ax={ax} act={p.axActs} />
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

// ── Part A notes (T1549-1575) ───────────────────────────────────────────────

export function NotesTab(p: Common & { q: QueryState<{ real: true; rows: PartANoteRow[] } | null>; rows: { row: NoteRow; ax: Ax }[]; allRows: { row: NoteRow; ax: Ax }[]; sign: (residentId: string) => Promise<boolean> }) {
  if (p.q.data === null && !p.q.loading && !p.q.error) {
    return <EmptyState title="Part A notes are not connected yet" body="Hours since each Part A resident's last signed note appear here once the route is served." waitingOn="GET /worklist/part-a" />;
  }
  const all = p.allRows.map((r) => r.row);
  const late = all.filter((x) => x.late && !x.signed).length;
  const soon = all.filter((x) => x.soon && !x.signed).length;
  return (
    <Gate q={p.q}>
      <Strip
        cells={[
          cell('Past 24 hours', String(late), late ? 'a denied certification starts here' : 'every note inside the window', late ? '#B3261E' : '#1F6B52'),
          cell('Due within 4 hours', String(soon), 'write before the window closes', soon ? '#3C454F' : '#22272E'),
          cell('Part A residents', String(all.length), 'skilled stay, note required daily', '#22272E'),
          cell('Owner', 'charge nurse', 'a note is a clinician signature, never the system', '#54606C', true),
        ]}
      />
      <div style={sx('min-width:0;display:flex;flex-direction:column;flex:1 1 auto;min-height:0;')}>
        <div
          title="Medicare Part A requires a note every 24 hours carrying recent vitals, why skilled services are needed, and today’s condition. Hours are counted from the last signed note."
          style={sx('flex:0 0 auto;font-size:17px;font-weight:800;letter-spacing:-.016em;')}
        >
          {late === 0 ? 'Every Part A note is inside its window' : `${late} note${late === 1 ? '' : 's'} past the 24-hour window`}
        </div>
        <div style={sx('flex:1 1 auto;min-height:150px;overflow-y:auto;min-width:0;margin-top:8px;padding-right:4px;')}>
          {p.rows.map(({ row: n, ax }) => (
            <div key={n.key} style={sx(n.rowStyle)}>
              <span style={sx('flex:1 1 240px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx('font-size:16px;font-weight:800;')}>
                  {n.name} <span style={sx('color:#54606C;font-weight:500;font-size:14px;')}>{n.room}</span>
                </span>
                <span style={sx(n.missingStyle)}>{n.missing}</span>
                <span style={sx(`${n.barTrack}max-width:260px;`)}>
                  <i style={sx(n.bar)} />
                </span>
              </span>
              <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:6px;')}>
                {n.open && (
                  <span style={sx(`font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:${n.clockColor};`)}>
                    <span data-wq-left={n.dueAt}>{n.clock}</span>
                  </span>
                )}
                {!p.ro && (
                  <A style={n.btnStyle} onClick={() => void p.sign(n.key)} disabled={p.writesOff || n.signed} title={p.offReason ?? undefined}>
                    {n.btnLabel}
                  </A>
                )}
              </span>
              <AxView ax={ax} act={p.axActs} />
            </div>
          ))}
        </div>
        <div style={sx('flex:0 0 auto;font-size:14px;color:#54606C;margin-top:11px;padding-top:9px;border-top:1px solid rgba(34,39,46,.12);line-height:1.45;max-width:96ch;text-wrap:pretty;')}>
          A note is written and signed by a clinician: Atlas drafts, it never signs.
        </div>
      </div>
    </Gate>
  );
}

// ── Requests (T1577-1642) ───────────────────────────────────────────────────

export function RequestsTab(
  p: Common & {
    q: QueryState<{ careRequests: CareRequest[]; real?: boolean }>;
    rows: { row: RequestRow; ax: Ax }[];
    allRows: RequestRow[];
    filter: 'Open' | 'All';
    setFilter: (f: 'Open' | 'All') => void;
    src: string;
    setSrc: (s: string) => void;
    brief: boolean;
    setBrief: (b: boolean) => void;
    ack: (id: string) => Promise<boolean>;
    done: (id: string) => Promise<boolean>;
    /** Care Staff: their own scheduled tasks, right under the requests (tasks first). */
    scheduled?: ReactNode;
    /** Care Staff: the folded Shift brief and More rows, under the tasks and above Ask Atlas. */
    folded?: ReactNode;
    /** Under Ask Atlas: credentials, the new medicine watch, survey items (V8). */
    extras?: ReactNode;
  },
) {
  const all = p.allRows;
  const cs = p.lens === 'staff';
  // V9 (design wNext5): Care Staff see their next five, most urgent first, with Show all.
  const [allOpen, setAllOpen] = useState(false);
  const capped = cs && !allOpen;
  const shownRows = capped ? p.rows.slice(0, 5) : p.rows;
  const nextN = Math.min(5, p.rows.filter((r) => r.row.isOpen).length);
  const open = all.filter((q) => q.isOpen);
  const urgent = open.filter((q) => q.urg === 'URGENT').length;
  const counts: [string, number, string][] = [
    ['urgent', urgent, '#B3261E'],
    ['open', open.length - urgent, '#3C454F'],
    ['closed this shift', all.length - open.length, '#1F6B52'],
  ];
  const ranked = open.slice().sort((a, b) => (a.urg !== b.urg ? (a.urg === 'URGENT' ? -1 : 1) : a.since - b.since));
  const oldest = open.reduce<RequestRow | null>((m, q) => (!m || q.since < m.since ? q : m), null);
  const recNote = 'Acknowledgments and dose records are written to the care record through /care-requests and /med-pass.';
  return (
    <Gate q={p.q}>
      <div style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;min-width:0;')}>
        {counts.map(([k, v, c]) => (
          <CountCell key={k} v={String(v)} k={k} c={v || k === 'closed this shift' ? c : '#22272E'} />
        ))}
        {p.q.data && <HonestyText flags={[p.q.data.real === undefined ? null : { real: p.q.data.real }]} figure="Care requests" hide={['MEASURED']} title={recNote} style="font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;" />}
        <span style={sx('flex:1 1 10px;')} />
        <A style="display:inline-flex;align-items:center;min-height:36px;padding:0 12px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#2F5C97;border:1px solid rgba(47,92,151,.3);" onClick={() => p.setBrief(!p.brief)}>
          Request summary
        </A>
        {(['Open', 'All'] as const).map((f) => (
          <A
            key={f}
            onClick={() => p.setFilter(f)}
            style={
              'display:inline-flex;align-items:center;height:29px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:' + (p.filter === f ? '700' : '500') + ';text-decoration:none;color:' + (p.filter === f ? '#fff' : '#54606C') +
              ';background:' + (p.filter === f ? '#2F5C97' : '#FCFAF4') + ';border:1px solid ' + (p.filter === f ? '#2F5C97' : 'rgba(34,39,46,.14)') + ';'
            }
          >
            {f}
          </A>
        ))}
      </div>
      {/* V9: Care Staff reach the source filters through Search & shortcuts (design wReqTools). */}
      {(!cs || p.tools) && (<>
      <div title="Family concierge requests land in the same queue as the call button. A ride or a refill is not a safety press, so the source is on every row." style={sx('display:flex;align-items:center;gap:3px;flex-wrap:wrap;row-gap:4px;margin-top:8px;min-width:0;')}>
        {['All', 'Call button', 'Atlas', 'Family concierge'].map((s) => (
          <A
            key={s}
            onClick={() => p.setSrc(s)}
            style={'display:inline-flex;align-items:center;gap:6px;height:25px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;' + (p.src === s ? 'background:rgba(79,138,214,.13);color:#2F5C97;' : 'color:#54606C;')}
          >
            {s === 'All' ? 'All sources' : s}
            <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;opacity:.72;')}>{String(s === 'All' ? all.length : all.filter((q) => q.src === s).length)}</span>
          </A>
        ))}
      </div>
      <div
        title="Repeat readings on the same resident collapse into one line, and anything below your severity floor never leaves the building. The count of what was held back is not logged yet, so we are not going to print a number for it."
        style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:8px;font-size:14px;color:#3C454F;')}
      >
        <span>{`${all.length} request${all.length === 1 ? '' : 's'} reached a person this shift`}</span>
        <BuildLabel><span style={sx('display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:#3C454F;background:rgba(34,39,46,.15);')}>NOT LOGGED YET</span></BuildLabel>
      </div>
      </>)}
      {p.brief && (
        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;margin-top:12px;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#2F5C97;font-weight:700;')}>Request summary</span>
            <span style={sx('font-size:14px;color:#54606C;')}>what needs you now</span>
          </div>
          <div style={sx('margin-top:10px;display:grid;gap:7px;')}>
            {ranked.slice(0, 4).map((q, i) => (
              <div key={q.id} style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:6px;font-size:16px;color:#22272E;min-width:0;')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#54606C;flex:0 0 14px;')}>{String(i + 1)}</span>
                <span style={sx('font-weight:700;')}>{q.name}</span>
                <span style={sx('font-size:14px;color:#54606C;')}>
                  {q.room} &middot; {q.kind}
                </span>
                <span style={sx('flex:1;')} />
                <span style={sx('font-size:14px;color:#54606C;')}>
                  {q.urg === 'URGENT' ? (q === oldest ? 'urgent · longest waiting' : 'urgent · needs someone now') : q === oldest ? 'prompt · longest waiting' : 'prompt · can wait for the round'}
                </span>
                <span style={sx('font-size:14px;color:#54606C;')}>{q.age}</span>
              </div>
            ))}
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:10px;')}>{`Ordered urgent first, then by age. ${urgent} urgent of ${open.length} open. This brief is derived from the open requests on this page, not a separate model.`}</div>
        </div>
      )}
      {capped && nextN > 0 && (
        <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-top:12px;')}>
          <span style={sx('font-size:18px;font-weight:800;color:#22272E;')}>{`Your next ${nextN}`}</span>
          <span style={sx('font-size:17px;color:#54606C;')}>Most urgent first. Each one moves to the next aide, then the charge nurse, if it waits too long.</span>
        </div>
      )}
      <div style={sx('margin-top:8px;min-width:0;')}>
        {shownRows.map(({ row: q, ax }) => (
          <div key={q.id} style={sx(q.rowStyle)}>
            <span style={sx('flex:1 1 240px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
              <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                <span style={sx('font-size:17px;font-weight:800;')}>{q.name}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{q.room}</span>
                <span style={sx(q.urgStyle)}>{q.urg}</span>
              </span>
              <span style={sx('font-size:16px;color:#22272E;line-height:1.4;')}>
                <b>{q.kind}</b> &middot; <span style={sx('font-style:italic;color:#3C454F;')}>{q.quote}</span>
              </span>
              <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
                <BuildTagged tag={q.src}><span style={sx(q.srcStyle)}>{q.src}</span></BuildTagged>
                <span style={sx(q.stStyle)}>{q.st}</span>
              </span>
              {p.ladder?.(q.id)}
            </span>
            <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:6px;')}>
              {q.isOpen && (
                <span style={sx(`font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:${q.waitColor};`)}>
                  <span data-wq-since={q.since}>{q.clock}</span>
                </span>
              )}
              <span style={sx('display:flex;gap:6px;')}>
                {!p.ro && q.showAck && (
                  <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#FCFAF4;border:1px solid rgba(34,39,46,.22);color:#22272E;" onClick={() => void p.ack(q.id)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                    Acknowledge
                  </A>
                )}
                {!p.ro && q.showDone && (
                  <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#1D3F6B;color:#FFFFFF;" onClick={() => void p.done(q.id)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                    Done
                  </A>
                )}
              </span>
            </span>
            <AxView ax={ax} act={p.axActs} />
          </div>
        ))}
        {capped && p.rows.length > 5 && (
          <div style={sx('display:flex;justify-content:center;margin-top:10px;')}>
            <A style="display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:10px;font-size:17px;font-weight:700;text-decoration:none;color:#264C7E;border:1px solid rgba(47,92,151,.35);" onClick={() => setAllOpen(true)}>
              {`Show all ${p.rows.length}`}
            </A>
          </div>
        )}
      </div>
      {/* Empty only when the filter and source leave nothing: a view (At risk, Handed off) or a snooze
          just hides rows (design wReqEmpty is counted before the action layer's filter). */}
      {p.allRows.filter((q) => (p.filter === 'All' || q.isOpen) && (p.src === 'All' || q.src === p.src)).length === 0 && (
        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;margin-top:13px;font-size:16px;color:#54606C;')}>
          {p.filter === 'Open' ? 'Nothing open. Every request this shift has been closed.' : 'No requests recorded this shift.'}
        </div>
      )}
      {p.scheduled}
      {/* V9: tasks first; the folded rows, Ask Atlas, the V8 cards and the ladder sit under them. */}
      <div style={sx('display:flex;flex-direction:column;gap:8px;margin-top:12px;min-width:0;')}>
        {p.folded}
        <AskAtlasBar
          label="Worklist ask Atlas wrAsk"
          placeholder="Ask these requests, or say “Hey Siri, ask Aithora who’s waiting”"
          {...requestsAsk(all, p.ro, (id) => void p.ack(id))}
          note="Atlas reads requests and drafts replies. Nothing goes to a resident until you press Send."
          mb
        />
      </div>
      {p.extras}
      {p.movingUp}
    </Gate>
  );
}

// ── Med pass (T1644-1673) ───────────────────────────────────────────────────

// AF-1611 (Andrew, 2026-10-08): Given records at once (no dialog) with a 10-second Undo; Missed
// (not given) always asks why; a recorded dose is corrected only by Strike Out with a reason;
// one medicine at a time (no Give all). Per the order's service level (2026-10-13): an
// Assistance order offers Assisted · taken and Not taken; a self-administered order offers
// only an observation note; third-party orders never reach this list. The server decides who
// may record what and its refusal is shown word for word.
export function MedPassTab(p: Common & {
  q: QueryState<{ meds: MedPassRow[]; real?: boolean }>;
  rows: { row: MedRow; ax: Ax }[];
  allRows: { row: MedRow; ax: Ax }[];
  given: (m: MedPassRow) => void;
  notGiven: (m: MedPassRow) => void;
  strike: (m: MedPassRow) => void;
  /** Assisted · taken (an Assistance order). */
  assisted: (m: MedPassRow) => void;
  /** Not taken: an Assistance order's refused or missed, with the reason. */
  notTaken: (m: MedPassRow) => void;
  /** An observation note on a self-administered order. */
  observe: (m: MedPassRow) => void;
  /** V9, Facility Admin: the "Also today" row above the doses. */
  alsoToday?: ReactNode;
  /** V9: under the doses, the new medicine watch and the medicine safety cards. */
  after?: ReactNode;
}) {
  const all = p.allRows.map((r) => r.row);
  const toGive = all.filter((x) => x.level !== 'self_administered');
  const late = toGive.filter((x) => !x.recorded && x.due !== null && x.due < p.now).length;
  const counts: [string, number, string][] = [
    ['late', late, late ? '#B3261E' : '#22272E'],
    ['still to give', toGive.filter((x) => !x.recorded && !x.prn).length - late, '#3C454F'],
    ['as needed', toGive.filter((x) => !x.recorded && x.prn).length, '#54606C'],
    ['recorded this pass', all.filter((x) => x.recorded).length, '#1F6B52'],
  ];
  return (
    <Gate q={p.q}>
      <div data-screen-label="Worklist med pass" style={sx('min-width:0;display:flex;flex-direction:column;gap:8px;')}>
        {p.alsoToday}
        <AskAtlasBar
          label="Worklist ask Atlas wmAsk"
          placeholder="Ask this med pass: what’s late, what’s next, who refused"
          {...medsAsk(all)}
          note="Questions only. Atlas never records a dose; the Given or Missed tap is the nurse’s signature."
          mb
        />
        <div style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;min-width:0;')}>
          {counts.map(([k, v, c]) => (
            <CountCell key={k} v={String(v)} k={k} c={c} />
          ))}
          {p.q.data && (
            <HonestyText
              flags={[p.q.data.real === undefined ? null : { real: p.q.data.real }]}
              figure="Med pass times"
              hide={['MEASURED']}
              suffix=" times"
              title="Dose times come from the medication schedule; a dose record is written to the care record."
              style="font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;"
            />
          )}
          <span style={sx('flex:1 1 10px;')} />
          <span style={sx('font-size:14px;color:#54606C;')}>{p.ro ? '' : 'A dose record is a nurse signature'}</span>
        </div>
        <div style={sx('min-width:0;')}>
          {p.rows.map(({ row: m, ax }) => (
            <div key={m.key} style={sx(m.rowStyle)}>
              <span style={sx('flex:1 1 240px;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:17px;font-weight:800;')}>{m.res}</span>
                  <span style={sx('font-size:16px;color:#54606C;')}>{m.room}</span>
                </span>
                <span style={sx('font-size:16px;color:#22272E;')}>
                  <b>{m.name}</b> <span style={sx('color:#54606C;')}>&middot; {m.dose}</span>
                </span>
                <CoverTag text={p.coverTag?.(`medpass:${m.med.medication_id}`) ?? null} />
              </span>
              <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:6px;')}>
                {!m.recorded && (
                  <span data-wq-due={m.due ?? ''} style={sx(`font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:${m.dueColor};`)}>
                    {m.dueText}
                  </span>
                )}
                {!m.recorded && !p.ro && m.level === 'administer' && (
                  <span style={sx('display:flex;gap:6px;')}>
                    <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#1D3F6B;color:#FFFFFF;" onClick={() => p.given(m.med)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                      Given
                    </A>
                    <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#FCFAF4;border:1px solid rgba(34,39,46,.22);color:#22272E;" onClick={() => p.notGiven(m.med)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                      Missed
                    </A>
                  </span>
                )}
                {!m.recorded && !p.ro && m.level === 'assistance' && (
                  <span style={sx('display:flex;gap:6px;')}>
                    <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#1D3F6B;color:#FFFFFF;" onClick={() => p.assisted(m.med)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                      Assisted · taken
                    </A>
                    <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#FCFAF4;border:1px solid rgba(34,39,46,.22);color:#22272E;" onClick={() => p.notTaken(m.med)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                      Not taken
                    </A>
                  </span>
                )}
                {!m.recorded && !p.ro && m.level === 'self_administered' && (
                  <span style={sx('display:flex;gap:6px;')}>
                    <A style="display:inline-flex;align-items:center;justify-content:center;min-height:34px;padding:0 13px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#FCFAF4;border:1px solid rgba(34,39,46,.22);color:#22272E;" onClick={() => p.observe(m.med)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                      Add a note
                    </A>
                  </span>
                )}
                {m.recorded && <span style={sx(m.stStyle)}>{m.st}</span>}
                {m.recorded && !p.ro && m.med.administration_id && (
                  <A style="font-size:14px;font-weight:700;color:#B3261E;text-decoration:none;" onClick={() => p.strike(m.med)} disabled={p.writesOff} title={p.offReason ?? undefined}>
                    Strike out
                  </A>
                )}
              </span>
              <AxView ax={ax} act={p.axActs} />
            </div>
          ))}
        </div>
        {p.after}
      </div>
    </Gate>
  );
}

// ── Nutrition (T1675-1783) ──────────────────────────────────────────────────

export function NutritionTab(
  p: Common & {
    q: QueryState<{ worklist: NutritionWorklist } | null>;
    rows: { row: NutView; ax: Ax }[];
    allRows: NutView[];
    sel: number;
    setSel: (i: number) => void;
    wt: string;
    setWt: (v: string) => void;
    ch: string;
    setCh: (v: string) => void;
    roleName: string;
    record: (residentId: string, lb: number, reps: number) => Promise<boolean>;
  },
) {
  // In a seeded building scope (no banner, no "demo" on screen): no SAMPLE chip on each figure.
  const tag = useShownHonesty();
  const buildLabels = useBuildLabels();
  if (p.q.data === null && !p.q.loading && (!p.q.error || (p.ro && p.q.error.kind === 'forbidden'))) {
    return <EmptyState title="Nutrition reads one building at a time" body="The nutrition worklist is served for the signed-in building only." waitingOn="GET /worklist/nutrition?facility= for Aithora staff" />;
  }
  const w = p.q.data?.worklist;
  const all = p.allRows;
  const codable = all.filter((n) => n.state === 'CODABLE').length;
  const fnOnly = all.filter((n) => n.state === 'FUNCTION ONLY').length;
  const measured = w?.measuredCount ?? all.filter((n) => (n.row.observations ?? []).length).length;
  const residents = w?.residentCount ?? null;
  const src = w?.real ? 'RECORDED' : measured ? 'PART RECORDED' : 'SAMPLE · NO SOURCE';
  const lead = `${codable} codable in an open MDS window · ${fnOnly} flagged on function alone. ${codable} codable resident${codable === 1 ? '' : 's'} × $${NUT_PPD}/day published industry average for missed Nursing/NTA coding = $${codable * NUT_PPD}/day at risk while the window is open. That rate is an industry average, not this facility’s measured exposure.`;
  const srcNote = 'Seed weights, BMI, chair-stand counts, GLP-1 prescriptions and ARD dates are illustrative until a person records a measurement here; a recorded measurement carries who took it.';
  const wtN = parseFloat(p.wt);
  const chN = parseInt(p.ch, 10);
  const wtOk = !isNaN(wtN) && wtN >= 60 && wtN <= 500;
  const chOk = !isNaN(chN) && chN >= 0 && chN <= 40;
  const canRec = wtOk && chOk && !p.writesOff;
  const err = p.wt === '' && p.ch === '' ? '' : !wtOk ? 'Weight must be between 60 and 500 lb.' : !chOk ? 'Chair-stand must be between 0 and 40 reps in 30 seconds.' : '';
  const inputStyle = 'width:100%;height:26px;border:1px solid rgba(34,39,46,.2);border-radius:7px;padding:0 10px;font-size:16px;color:#22272E;background:#fff;font-family:inherit;box-sizing:border-box;';
  return (
    <Gate q={p.q}>
      <Strip
        cells={[
          cell('Triggered', String(codable + fnOnly), codable + fnOnly ? 'weight, function or both' : 'no trigger this window', codable + fnOnly ? '#B3261E' : '#1F6B52'),
          cell('Codable', String(codable), 'meets the MDS K threshold', codable ? '#3C454F' : '#22272E'),
          cell('Function only', String(fnOnly), 'decline without weight loss', '#22272E'),
          // How many rows are measured vs sample is a build label: Developer and Super Admin only.
          ...(buildLabels ? [cell('Measured here', `${measured} of ${all.length}`, 'the rest are still SAMPLE', '#54606C', true)] : []),
        ]}
      />
      <div style={sx('min-width:0;')}>
        <span style={sx('font-size:19px;font-weight:700;color:#22272E;letter-spacing:-.01em;')}>Nutrition &amp; weight</span>{' '}
        <BuildTagged tag={src}><span style={sx('display:inline-block;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;border-radius:4px;padding:1px 5px;margin-left:9px;color:#3C454F;background:rgba(34,39,46,.14);vertical-align:middle;')}>{src}</span></BuildTagged>
        <div title={`${lead} ${srcNote}`} style={sx('font-size:14px;color:#3C454F;margin-top:4px;line-height:1.5;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>
          {residents != null
            ? `${measured} of ${residents} residents have a recorded measurement on this instrument. The other ${residents - measured} are unscreened, not clear.`
            : `${measured} residents have a recorded measurement on this instrument; the building's resident count is not reported yet.`}
        </div>
      </div>
      <div style={sx('margin-top:10px;min-width:0;')}>
        <div style={sx('display:grid;gap:0;min-width:0;')}>
          {p.rows.map(({ row: n, ax }) => {
            const isSel = n.i === p.sel;
            const obs = (n.row.observations ?? []).slice().reverse();
            const docs = n.row.docs ?? [];
            return (
              <div key={n.row.residentId} style={sx('display:contents;')}>
                <button
                  type="button"
                  className="wl-a"
                  onClick={() => p.setSel(n.i)}
                  aria-pressed={isSel}
                  style={sx('display:block;text-decoration:none;min-height:44px;padding:11px 10px;border-top:1px solid rgba(34,39,46,.10);' + (isSel ? 'background:rgba(47,92,151,.07);box-shadow:inset 3px 0 0 #2F5C97;' : ''))}
                >
                  <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
                    <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{n.name}</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>{n.room}</span>
                    <span style={sx('flex:1;min-width:6px;')} />
                    <span style={sx(n.stateStyle)}>{n.state}</span>
                  </span>
                  <span style={sx('display:block;font-size:14px;color:#3C454F;margin-top:5px;')}>
                    {n.wt} &middot; {n.l30}
                  </span>
                  <span style={sx(`display:block;margin-top:3px;${n.obsStyle}`)}>{n.obsLine}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:3px;')}>{n.trig}</span>
                  <span style={sx(`display:block;margin-top:4px;${n.windowStyle}`)}>
                    {n.window}
                    {n.inWindow && (
                      <>
                        {' '}
                        &middot; ARD{' '}
                        <span data-wq-due={n.dueAt ?? ''} style={sx('font-variant-numeric:tabular-nums;color:' + ((n.dueAt ?? 0) < p.now ? '#B3261E' : '#3C454F') + ';')}>
                          {n.dueAt ? dueFmt(n.dueAt - p.now) : 'due'}
                        </span>
                      </>
                    )}
                  </span>
                </button>
                <AxView ax={ax} act={p.axActs} />
                {isSel && (
                  <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;min-width:0;')}>
                    <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
                      <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>{n.exposure}</span>
                      {tag(n.wtSrc) && <BuildTagged tag={n.wtSrc}><span style={sx(n.wtSrcStyle)}>{n.wtSrc}</span></BuildTagged>}
                    </div>
                    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:12px;margin-top:13px;')}>
                      <div style={sx('min-width:0;')}>
                        <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;')}>
                          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Weight</span>
                          {tag(n.wtSrc) && <BuildTagged tag={n.wtSrc}><span style={sx(n.wtSrcStyle)}>{n.wtSrc}</span></BuildTagged>}
                        </div>
                        <div style={sx('font-size:16px;color:#22272E;font-weight:600;margin-top:3px;')}>{n.wt}</div>
                        <div style={sx('font-size:14px;color:#54606C;margin-top:2px;')}>
                          {n.l30} &middot; {n.l180}
                        </div>
                      </div>
                      <div style={sx('min-width:0;')}>
                        <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;')}>
                          <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Chair-stand</span>
                          {tag(n.chairSrc) && <BuildTagged tag={n.chairSrc}><span style={sx(n.chairSrcStyle)}>{n.chairSrc}</span></BuildTagged>}
                        </div>
                        <div style={sx('font-size:16px;color:#22272E;font-weight:600;margin-top:3px;')}>{n.chair}</div>
                        <div style={sx(`font-size:14px;margin-top:2px;color:${n.chairColor};`)}>{n.chairNote}</div>
                      </div>
                      <div style={sx('min-width:0;')}>
                        <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>MDS window</div>
                        <div style={sx(`margin-top:3px;${n.windowStyle}`)}>{n.window}</div>
                        <div style={sx('font-size:14px;color:#54606C;margin-top:2px;')}>{n.trig}</div>
                      </div>
                    </div>
                    {n.hasGlp && (
                      <div style={sx('border:1px solid rgba(47,92,151,.28);background:rgba(47,92,151,.06);border-radius:10px;padding:11px 13px;margin-top:13px;')}>
                        <div style={sx('font-size:14px;font-weight:700;color:#2F5C97;')}>GLP-1: {n.glp}</div>
                        <div style={sx('font-size:14px;color:#3C454F;line-height:1.55;margin-top:4px;')}>{n.glpNote}</div>
                      </div>
                    )}
                    {!p.ro && <div style={sx('border:1px solid rgba(47,92,151,.24);background:rgba(47,92,151,.05);border-radius:10px;padding:13px 14px;margin-top:14px;')}>
                      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#2F5C97;font-weight:700;')}>Record a measurement</div>
                      <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto;gap:10px;align-items:end;margin-top:9px;')}>
                        <label style={sx('display:block;min-width:0;')}>
                          <span style={sx('display:block;font-size:14px;color:#54606C;margin-bottom:4px;')}>Weight (lb)</span>
                          <input type="number" value={p.wt} onChange={(e) => p.setWt(e.target.value)} placeholder="e.g. 148" style={sx(inputStyle)} disabled={p.writesOff} />
                        </label>
                        <label style={sx('display:block;min-width:0;')}>
                          <span style={sx('display:block;font-size:14px;color:#54606C;margin-bottom:4px;')}>Chair-stand (reps / 30s)</span>
                          <input type="number" value={p.ch} onChange={(e) => p.setCh(e.target.value)} placeholder="e.g. 9" style={sx(inputStyle)} disabled={p.writesOff} />
                        </label>
                        <A
                          style={'display:inline-flex;align-items:center;justify-content:center;height:26px;padding:0 15px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' + (canRec ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;')}
                          onClick={() => canRec && void p.record(n.row.residentId, wtN, chN)}
                          disabled={!canRec}
                          title={p.offReason ?? undefined}
                        >
                          Record
                        </A>
                      </div>
                      <div style={sx('font-size:14px;color:#3C454F;line-height:1.5;margin-top:7px;')}>{err}</div>
                      <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:3px;')}>
                        {`Weekly, same scale, same conditions. Chair-stand is the 30-second sit-to-stand count. Recorded as ${p.roleName}. An interval loss needs two weights at least 7 days apart; a shorter span is logged but does not move the MDS finding.`}
                      </div>
                      {obs.length > 0 && (
                        <div style={sx('border-top:1px solid rgba(34,39,46,.1);margin-top:11px;padding-top:9px;display:grid;gap:6px;')}>
                          {obs.map((o, i) => (
                            <div key={i} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
                              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:600;color:#22272E;')}>{`${Math.round(o.lb)} lb · ${o.chair} reps`}</span>
                              <span style={sx('font-size:14px;color:#54606C;')}>{`${o.by} · ${new Date(o.at).toLocaleString()}`}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>}
                    <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:14px;')}>Supporting documentation</div>
                    <div style={sx(`margin-top:4px;${n.docStyle}`)}>{n.docLine}</div>
                    <div style={sx('display:grid;gap:8px;margin-top:9px;')}>
                      {NUT_DOC.map((d, j) => {
                        const on = !!docs[j];
                        return (
                          <div key={d} style={sx('display:flex;align-items:flex-start;gap:10px;min-width:0;')}>
                            <span
                              style={sx(
                                'display:inline-flex;align-items:center;justify-content:center;width:17px;height:17px;flex:0 0 17px;border-radius:4px;font-size:14px;font-weight:700;color:#fff;border:1.5px solid ;white-space:nowrap;' +
                                  (on ? '#1F6B52' : 'rgba(34,39,46,.28)') + ';background:' + (on ? '#1F6B52' : 'transparent') + ';',
                              )}
                            >
                              {on ? '✓' : ''}
                            </span>
                            <span style={sx('min-width:0;')}>
                              <span style={sx('display:block;font-size:16px;color:#3C454F;line-height:1.5;')}>{d}</span>
                              <span style={sx(`display:block;font-size:14px;color:${on ? '#54606C' : '#3C454F'};`)}>{on ? 'on file' : 'missing, not codable without it'}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:13px;')}>
                      Aithora does not code and does not assert a diagnosis. It shows the finding, the MDS window, and which supporting documents exist. A dietitian and a physician document; the MDS nurse codes.
                    </div>
                    <div style={sx('font-size:14px;color:#3C454F;line-height:1.5;margin-top:6px;')}>
                      CMS is scrutinising coding growth in malnutrition, depression and swallowing disorders. A flag with its documentation attached is defensible; a flag without it is not worth filing.
                    </div>
                    <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:6px;')}>
                      Screening fires on weight trend or on the 30-second chair-stand, never on weight alone. In nursing-home cohorts 48% of residents with obesity had sarcopenia while only 8% had unplanned weight loss, so a weight-only trigger misses most of the risk.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Gate>
  );
}

// ── Benefits (T1785-1869) ───────────────────────────────────────────────────

export function BenefitsTab(
  p: Common & {
    q: QueryState<BenefitQueue>;
    rows: { b: BenefitItem; ax: Ax }[];
    all: BenefitItem[];
    kind: string;
    setKind: (k: string) => void;
    refOpen: boolean;
    setRefOpen: (v: boolean) => void;
    reference: { facility: BenefitReferenceRow[]; resident: BenefitReferenceRow[]; notes: { facility: string; resident: string; volatility: string; lead?: string; gap?: string } } | null;
    handle: (id: string) => Promise<boolean>;
    daysLeft: (b: BenefitItem) => number | null;
    isHandled: (b: BenefitItem) => boolean;
  },
) {
  const dl = (b: BenefitItem) => p.daysLeft(b) ?? 999;
  const open = (lo: number, hi: number) => p.all.filter((b) => !p.isHandled(b) && dl(b) >= lo && dl(b) <= hi).length;
  const weekN = p.all.filter((b) => !p.isHandled(b) && dl(b) <= 7).length;
  const canAct = p.lens === 'admin' || p.lens === 'staff';
  const shown = p.rows
    .filter(({ b }) => p.kind === 'All' || kindOfBenefit(b) === p.kind.toLowerCase())
    .sort((a, b) => (p.isHandled(a.b) ? 1 : 0) - (p.isHandled(b.b) ? 1 : 0) || dl(a.b) - dl(b.b));
  const BUCKETS: [string, number, number, string][] = [
    ['This week', -9999, 7, '#B3261E'],
    ['Next 30 days', 8, 30, '#3C454F'],
    ['Beyond 30 days', 31, 99999, '#54606C'],
  ];
  const KINDC: Record<string, string> = { rebate: '#2F5C97', benefit: '#1F6B52', recert: '#3C454F' };
  const RefLane = ({ title, rows, color, note, extra }: { title: string; rows: BenefitReferenceRow[]; color: string; note: string; extra?: string }) => (
    <div style={sx('padding:6px 0;min-width:0;')}>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>{title}</div>
      <div style={sx('display:grid;gap:11px;margin-top:11px;')}>
        {rows.map((b) => (
          <div key={b.k} style={sx('border-top:1px solid rgba(34,39,46,.09);padding-top:10px;min-width:0;animation:riseIn .28s cubic-bezier(.22,.61,.36,1);')}>
            <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;')}>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{b.k}</span>
              <span style={sx(`font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:${color};`)}>{b.v}</span>
              <span style={sx('flex:1;min-width:6px;')} />
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>{b.y}</span>
            </div>
            <div style={sx('font-size:14px;color:#3C454F;line-height:1.55;margin-top:4px;')}>{b.d}</div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:3px;')}>{b.s}</div>
          </div>
        ))}
      </div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:12px;')}>{note}</div>
      {extra && <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:6px;')}>{extra}</div>}
    </div>
  );
  return (
    <Gate q={p.q}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:baseline;gap:6px 18px;flex-wrap:wrap;min-width:0;')}>
        {(
          [
            ['This week', open(-9999, 7), open(-9999, 7) ? '#B3261E' : '#1F6B52'],
            ['Next 30 days', open(8, 30), '#3C454F'],
            ['Handled', p.all.filter(p.isHandled).length, '#1F6B52'],
          ] as [string, number, string][]
        ).map(([k, v, c]) => (
          <span key={k} style={sx('display:inline-flex;align-items:baseline;gap:6px;')}>
            <span data-countup="1" style={sx(`font-size:22px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;color:${c};`)}>{String(v)}</span>
            <span style={sx('font-size:16px;color:#3C454F;')}>{k}</span>
          </span>
        ))}
        {p.q.data && (
          <HonestyText
            flags={[p.q.data.basis || p.q.data.real !== undefined ? { basis: p.q.data.basis ?? null, real: p.q.data.real } : null]}
            figure="Benefits queue"
            title={p.reference?.notes.lead ? `${p.reference.notes.lead} ${p.reference.notes.gap ?? ''}` : undefined}
            style="display:inline-flex;align-items:center;font-size:14px;font-weight:700;letter-spacing:.09em;color:#3C454F;background:rgba(34,39,46,.13);border-radius:4px;padding:2px 7px;white-space:nowrap;"
          />
        )}
        <span style={sx('flex:1 1 20px;')} />
        <div role="tablist" aria-label="Kind" style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
          {['All', 'Rebate', 'Benefit', 'Recert'].map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={p.kind === k}
              className="wl-a"
              onClick={() => p.setKind(k)}
              style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' + (p.kind === k ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'))}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      <div title="One queue, grouped by deadline: the failure mode for rebates and benefits is a missed date, not a missed person. Filter by type; the ordering stays chronological." style={sx('font-size:17px;font-weight:700;color:#22272E;letter-spacing:-.01em;margin-top:6px;')}>
        {weekN ? `${weekN} deadline${weekN === 1 ? '' : 's'} this week` : 'Nothing due this week'}
      </div>
      <div style={sx('display:flex;flex-direction:column;min-width:0;margin-top:8px;')}>
        {BUCKETS.map(([name, lo, hi, tone]) => {
          const rows = shown.filter(({ b }) => dl(b) >= lo && dl(b) <= hi);
          return (
            <div key={name} style={sx('min-width:0;margin-bottom:10px;')}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.10);')}>
                <span style={sx(`font-size:14px;letter-spacing:.12em;text-transform:uppercase;font-weight:700;white-space:nowrap;color:${tone};`)}>{name}</span>
                <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>{String(rows.length)}</span>
              </div>
              {rows.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:10px 14px;')}>Nothing due in this window.</div>}
              {rows.map(({ b, ax }, ri) => {
                const done = p.isHandled(b);
                const d = p.daysLeft(b);
                const kind = kindOfBenefit(b);
                const who = b.residentLabel ?? 'Facility-wide';
                return (
                  <div
                    key={b.id}
                    style={sx(
                      'animation:riseIn .28s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(240, ri * 40) + 'ms;display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;min-height:44px;padding:11px 14px;border-bottom:1px solid rgba(34,39,46,.10);border-left:3px solid ' +
                        (!done && lo < 0 ? '#B3261E' : 'transparent') + ';' + (done ? 'opacity:.62;' : ''),
                    )}
                  >
                    <span style={sx('flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:3px;')}>
                      <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                        <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{who.charAt(0).toUpperCase() + who.slice(1)}</span>
                        <span style={sx('display:inline-flex;align-items:center;height:18px;padding:0 7px;border-radius:4px;font-size:14px;letter-spacing:.05em;font-weight:700;white-space:nowrap;color:#fff;background:' + KINDC[kind] + ';')}>{kind}</span>
                      </span>
                      <span style={sx('font-size:16px;color:#3C454F;')}>{b.title}</span>
                      <span style={sx('font-size:14px;color:#54606C;')}>{b.description ?? ''}</span>
                    </span>
                    <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:6px;')}>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:800;white-space:nowrap;color:' + (done ? '#1F6B52' : tone === '#54606C' ? '#22272E' : tone) + ';')}>
                        {done ? '✓ Handled' : d === null ? 'No date on file' : d <= 0 ? 'Due today' : `${d}${d === 1 ? ' day left' : ' days left'}`}
                      </span>
                      {canAct && !done && (
                        <A
                          style="display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 16px;border-radius:8px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;"
                          onClick={() => void p.handle(b.id)}
                          disabled={p.writesOff}
                          title={p.offReason ?? undefined}
                        >
                          Done
                        </A>
                      )}
                    </span>
                    <AxView ax={ax} act={p.axActs} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="wl-a"
        onClick={() => p.setRefOpen(!p.refOpen)}
        aria-expanded={p.refOpen}
        style={sx('display:flex;align-items:center;gap:8px;min-height:44px;padding:0 2px;text-decoration:none;border-top:1px solid rgba(34,39,46,.14);font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;width:100%;box-sizing:border-box;')}
      >
        {`${p.refOpen ? '▾' : '▸'}  Reference · facility rates & resident allowances`}
      </button>
      {p.refOpen &&
        (p.reference ? (
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:16px;align-items:start;')}>
            <RefLane title="Facility-side rates" rows={p.reference.facility} color="#3C454F" note={p.reference.notes.facility} />
            <RefLane title="Resident-side allowances" rows={p.reference.resident} color="#2F5C97" note={p.reference.notes.resident} extra={p.reference.notes.volatility} />
          </div>
        ) : (
          <EmptyState compact title="The published rates are not connected yet" body="Facility-side rates and resident-side allowances, each dated to its programme year, load from the reference route." waitingOn="GET /benefit-items/reference" />
        ))}
    </Gate>
  );
}

// ── Referrals (T1871-1958) ──────────────────────────────────────────────────

export function ReferralsTab(
  p: Common & {
    q: QueryState<{ referrals: Referral[]; real?: boolean }>;
    rows: { r: Referral; i: number; ax: Ax }[];
    all: Referral[];
    sel: number;
    setSel: (i: number) => void;
    drafts: Record<string, RefDraft>;
    setDraft: (id: string, d: RefDraft) => void;
    caseNo: Record<string, string>;
    setCaseNo: (id: string, v: string) => void;
    me: string;
    file: (r: Referral) => Promise<boolean>;
    saveCase: (r: Referral, v: string) => Promise<boolean>;
    refDone: (r: Referral, d: RefDraft | undefined) => number;
    wchk: typeof WCHK_T;
    /** The server's reporter checklist (restored from production). */
    checklist?: ReporterChecklistItem[];
    /** The building's residents, for a new referral (a Facility Admin only). */
    residents?: RosterResident[];
    create?: (residentId: string, observed: string) => Promise<boolean>;
    saveDraft?: (r: Referral) => Promise<boolean>;
  },
) {
  const { now } = p;
  const filed = (r: Referral) => r.status === 'SUBMITTED' || r.status === 'ACKNOWLEDGED' || r.status === 'CLOSED';
  const clockOf = (r: Referral) => {
    const start = r.suspicionAt ? Date.parse(r.suspicionAt) : Date.parse(r.created_at);
    const limit = r.limitHours ?? 24;
    const left = start + limit * 3_600_000 - now;
    return { start, limit, left, over: left < 0 };
  };
  const rows = p.rows
    .map((x) => ({ ...x, c: clockOf(x.r), done: p.refDone(x.r, p.drafts[x.r.id]), filed: filed(x.r) }))
    .sort((a, b) => Number(a.filed) - Number(b.filed) || Number(b.c.over) - Number(a.c.over) || a.i - b.i);
  // The counts cover every referral, not only the rows the view shows (design wRefCounts: a
  // snoozed or filtered row still counts).
  const allC = p.all.map((r) => ({ over: clockOf(r).over, filed: filed(r) }));
  const pastN = allC.filter((x) => x.over && !x.filed).length;
  const waitN = allC.filter((x) => !x.filed).length;
  const sel = rows.find((x) => x.i === p.sel) ?? null;
  const head = pastN ? `${pastN} referral${pastN === 1 ? '' : 's'} past the statutory limit` : waitN ? `${waitN} awaiting a reporter decision` : 'Every referral is filed';
  const lead = `${p.all.length} suspicion signal${p.all.length === 1 ? '' : 's'} surfaced by Guardian · ${p.all.filter(filed).length} filed · ${waitN} awaiting a reporter decision. Opening one drafts a referral; a person files it.`;
  return (
    <Gate q={p.q}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:baseline;gap:6px 18px;flex-wrap:wrap;min-width:0;')}>
        {(
          [
            ['Past the limit', pastN, pastN ? '#B3261E' : '#1F6B52'],
            ['Awaiting a reporter', waitN, '#3C454F'],
            ['Filed', rows.filter((x) => x.filed).length, '#1F6B52'],
          ] as [string, number, string][]
        ).map(([k, v, c]) => (
          <span key={k} style={sx('display:inline-flex;align-items:baseline;gap:6px;')}>
            <span data-countup="1" style={sx(`font-size:22px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;color:${c};`)}>{String(v)}</span>
            <span style={sx('font-size:16px;color:#3C454F;')}>{k}</span>
          </span>
        ))}
        <span title={lead} style={sx('display:inline-flex;align-items:baseline;gap:6px;')}>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>24 hours</span>
          <span style={sx('font-size:16px;color:#54606C;')}>Clock &middot; real time, does not pause at shift change</span>
        </span>
      </div>
      <div title={lead} style={sx('font-size:17px;font-weight:700;color:#22272E;letter-spacing:-.01em;margin-top:6px;')}>
        {head}
      </div>
      {p.create && (p.residents?.length ?? 0) > 0 && !p.ro && <NewReferral residents={p.residents!} create={p.create} disabled={p.writesOff} title={p.offReason ?? undefined} />}
      <div style={sx('display:flex;flex-direction:column;min-width:0;margin-top:8px;border-top:1px solid rgba(34,39,46,.10);')}>
        {rows.map((x) => {
          const { r, c, done } = x;
          const isSel = x.i === p.sel;
          const status = x.filed ? 'FILED' : done > 0 ? 'DRAFT' : 'NOT STARTED';
          const sc = x.filed ? '#1F6B52' : done > 0 ? '#2F5C97' : '#54606C';
          const sbg = x.filed ? 'rgba(47,138,111,.13)' : done > 0 ? 'rgba(47,92,151,.11)' : 'rgba(34,39,46,.06)';
          const sevColor = r.severity === 'LOW' ? '#54606C' : r.severity === 'MEDIUM' ? '#8A5A00' : '#B3261E';
          const imminent = !c.over && c.left < 3_600_000;
          return (
            <div key={r.id} style={sx('min-width:0;')}>
              <button
                type="button"
                className="wl-a"
                aria-expanded={isSel}
                onClick={() => p.setSel(isSel ? -1 : x.i)}
                style={sx(
                  'animation:riseIn .28s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(280, x.i * 40) + 'ms;display:flex;align-items:center;gap:10px 16px;flex-wrap:wrap;text-decoration:none;min-height:44px;padding:11px 14px;border-bottom:1px solid rgba(34,39,46,.10);border-left:3px solid ' +
                    (isSel ? '#2F5C97' : c.over && !x.filed ? '#B3261E' : 'transparent') + ';background:' + (isSel ? 'rgba(47,92,151,.06)' : 'transparent') + ';' + (x.filed && !isSel ? 'opacity:.72;' : '') + 'width:100%;box-sizing:border-box;',
                )}
              >
                <span style={sx('flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:3px;')}>
                  <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                    <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{r.residentName ?? 'Resident'}</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>{r.room ? `Room ${r.room}` : (r.facilityName ?? '')}</span>
                    <span style={sx(`display:inline-flex;align-items:center;white-space:nowrap;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;border-radius:4px;padding:2px 7px;color:${sc};background:${sbg};`)}>{status}</span>
                  </span>
                  <span style={sx('font-size:16px;color:#3C454F;')}>{r.signalLabel ?? 'Suspicion recorded'}</span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span style={sx(`font-weight:700;letter-spacing:.08em;color:${sevColor};`)}>{r.severity ?? 'UNRATED'}</span> &middot; {`${done} of ${p.wchk.length} complete`} &middot; {ageOf(now - c.start)}
                  </span>
                </span>
                <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:4px;')}>
                  <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + (x.filed ? '#1F6B52' : c.over ? '#B3261E' : imminent ? '#3C454F' : '#22272E') + ';')}>
                    {x.filed ? 'Filed' : c.over ? `Past the ${c.limit}h limit` : `${wqFmt(c.left)} left of ${c.limit}h`}
                  </span>
                  <span style={sx('font-size:14px;color:#2F5C97;font-weight:600;')}>{isSel ? 'Close' : x.filed ? 'View record' : 'Open referral'}</span>
                </span>
              </button>
              <AxView ax={x.ax} act={p.axActs} />
              {isSel && sel && <RefPanel {...p} x={sel} />}
            </div>
          );
        })}
      </div>
      {rows.length === 0 && (
        <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;margin-top:13px;font-size:16px;color:#54606C;')}>
          No suspicion signals are awaiting a reporter decision.
        </div>
      )}
    </Gate>
  );
}

/**
 * A new referral from a blank form (restored from production, reporter_view.dart:114). The
 * server still needs a qualifying basis: what the staff member observed, in their words.
 */
function NewReferral({ residents, create, disabled, title }: { residents: RosterResident[]; create: (residentId: string, observed: string) => Promise<boolean>; disabled: boolean; title?: string }) {
  const [open, setOpen] = useState(false);
  const [rid, setRid] = useState('');
  const [observed, setObserved] = useState('');
  const ok = !!rid && observed.trim().length >= 20 && !disabled;
  if (!open) {
    return (
      <div style={sx('margin-top:6px;')}>
        <A onClick={() => setOpen(true)} disabled={disabled} title={title} style="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;">
          + New referral
        </A>
      </div>
    );
  }
  return (
    <div data-testid="referral-new" style={sx('margin-top:8px;padding:12px 14px;border:1px solid rgba(34,39,46,.14);border-radius:10px;background:#FCFAF4;display:flex;flex-direction:column;gap:8px;min-width:0;')}>
      <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>New referral</span>
      <select aria-label="Resident" value={rid} onChange={(e) => setRid(e.target.value)} style={sx('height:32px;max-width:320px;font-family:inherit;font-size:16px;border:1px solid rgba(34,39,46,.16);border-radius:7px;background:#fff;padding:0 8px;')}>
        <option value="">Choose the resident</option>
        {residents.map((r) => <option key={r.id} value={r.id}>{r.preferred_name || r.name || 'Resident'}</option>)}
      </select>
      <textarea
        aria-label="What you observed"
        value={observed}
        onChange={(e) => setObserved(e.target.value)}
        placeholder="What you saw or heard, in your own words (20 characters or more). Do not name a cause."
        style={sx('width:100%;box-sizing:border-box;font-family:inherit;font-size:16px;line-height:1.5;color:#22272E;background:#fff;border:1px solid rgba(34,39,46,.16);border-radius:8px;padding:8px 10px;min-height:64px;resize:vertical;outline:none;')}
      />
      <div style={sx('display:flex;gap:10px;align-items:center;flex-wrap:wrap;')}>
        <A
          onClick={() => ok && void create(rid, observed.trim()).then((done) => { if (done) { setOpen(false); setRid(''); setObserved(''); } })}
          disabled={!ok}
          title={title}
          style={'display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;' + (ok ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.06);color:#54606C;')}
        >
          Open the referral
        </A>
        <A onClick={() => setOpen(false)} style="font-size:14px;font-weight:700;color:#3C454F;text-decoration:none;">
          Not now
        </A>
        <span style={sx('font-size:14px;color:#54606C;')}>It opens as a draft with your observation as the narrative. A person files it.</span>
      </div>
    </div>
  );
}

function ageOf(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

function RefPanel(
  p: Parameters<typeof ReferralsTab>[0] & {
    x: { r: Referral; done: number; filed: boolean };
  },
) {
  const { r } = p.x;
  const d = p.drafts[r.id] ?? {};
  const cl = r.checklist ?? {};
  const locked = p.x.filed;
  const narr = d.narrative ?? r.narrative ?? '';
  const phys = d.phys ?? (cl.preserve_evidence === 'na' || cl.preserve_evidence === 'attached' ? (cl.preserve_evidence as 'na' | 'attached') : undefined);
  const tick = (k: string) => (d.ticks?.[k] ?? cl[k]) === true;
  // The server's optional steps the design's four requirements do not already cover. The
  // reporter records contacting the agency by filing, so that step is not ticked here.
  const covered = new Set([...p.wchk.map((w) => w.key), 'contact_aps']);
  const extra = (p.checklist ?? []).filter((ck) => !ck.required && !covered.has(ck.key));
  const ready = p.refDone(r, d) === p.wchk.length;
  const c = r.contacts ?? null;
  const inScope = r.inScope1150B === true;
  const who = inScope ? 'the survey agency' : c?.apsLabel ? c.apsLabel : 'the investigating agency';
  const law = inScope
    ? r.limitHours === 2
      ? 'These events caused serious bodily injury, so Section 1150B gives you 2 hours from forming the suspicion: immediately, on real clock time, not business hours. Report to local law enforcement and the State Survey Agency; Aithora does not file for you.'
      : 'Section 1150B gives you 24 hours from forming the suspicion, on real clock time, not business hours. Report to local law enforcement and the State Survey Agency; Aithora does not file for you.'
    : `${c?.label ?? 'The state'} requires a report immediately, and no later than 24 hours after suspicion. This is an assisted-living resident, so the federal 2-hour rule does not apply: report to ${c?.aps ?? 'the state agency'}. Aithora does not file for you.`;
  const readyNote = locked
    ? 'This report is filed. The record is append-only: a correction is added, never written over.'
    : ready
      ? 'Recording this stamps your name, your role and the time onto the referral and locks the narrative.'
      : `${p.wchk.length - p.refDone(r, d)} of ${p.wchk.length} requirements outstanding.`;
  const physBtn = (val: 'attached' | 'na', label: string) => (
    <A
      onClick={() => p.setDraft(r.id, { phys: val })}
      disabled={p.writesOff}
      style={
        'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:6px;text-decoration:none;font-size:14px;font-weight:600;border:1px solid ;white-space:nowrap;' +
        (phys === val ? '#2F5C97' : 'rgba(34,39,46,.16)') + ';color:' + (phys === val ? '#2F5C97' : '#54606C') + ';background:' + (phys === val ? 'rgba(47,92,151,.09)' : '#FCFAF4') + ';'
      }
    >
      {label}
    </A>
  );
  const recordedAt = r.submitted_at ? new Date(r.submitted_at).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div style={sx('padding:14px 16px 18px;min-width:0;background:rgba(47,92,151,.04);border-left:3px solid #2F5C97;border-bottom:1px solid rgba(34,39,46,.10);')}>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:12px;')}>
        <div style={sx('min-width:0;')}>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Jurisdiction</div>
          <div style={sx('font-size:16px;color:#22272E;font-weight:600;margin-top:3px;')}>
            {c ? (inScope ? `${c.label} · State Survey Agency + law enforcement` : `${c.label} · ${c.apsLabel}`) : (r.jurisdiction ?? 'Not recorded yet')}
          </div>
        </div>
        <div style={sx('min-width:0;')}>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>{inScope ? 'Reporting contacts' : `${c?.apsLabel ?? 'Agency'} contact`}</div>
          <div style={sx('font-size:16px;color:#22272E;font-weight:600;margin-top:3px;')}>
            {c ? (inScope ? `${c.survey ?? 'State survey agency'} · 24/7 intake, plus at least one local law-enforcement agency` : `${c.aps} · assisted-living licensure path`) : (r.aps_contact ?? 'Not recorded yet')}
          </div>
        </div>
      </div>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:14px;')}>Objective observations</div>
      <div style={sx('font-size:14px;color:#3C454F;line-height:1.5;margin-top:4px;')}>{`Write only what was observed or said. Do not name a cause or a person as responsible: that determination is for ${who} to make, not you.`}</div>
      <textarea
        value={narr}
        onChange={(e) => p.setDraft(r.id, { narrative: e.target.value })}
        readOnly={locked || p.writesOff}
        aria-label="Objective observations"
        style={sx('width:100%;box-sizing:border-box;font-family:inherit;font-size:16px;line-height:1.6;color:#22272E;background:' + (locked ? 'rgba(34,39,46,.04)' : '#fff') + ';border:1px solid rgba(34,39,46,.16);border-radius:8px;padding:10px 12px;min-height:86px;resize:vertical;outline:none;')}
        placeholder="What was observed, and what the resident said."
      />
      <div style={sx('font-size:14px;color:#54606C;margin-top:4px;')}>{`${narr.trim().length} characters · 40 minimum`}</div>
      <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:14px;')}>Required before filing</div>
      <div style={sx('display:grid;gap:9px;margin-top:8px;')}>
        {p.wchk.map((ck) => {
          const auto = ck.kind === 'auto';
          const on = auto ? narr.trim().length >= 40 : ck.kind === 'choice' ? !!phys : tick(ck.key);
          const note = auto ? (on ? 'recorded' : ck.hint ?? '') : ck.kind === 'choice' ? (phys === 'na' ? 'marked not applicable' : phys === 'attached' ? 'photograph attached' : 'no answer yet') : '';
          return (
            <div key={ck.key} style={sx('min-width:0;')}>
              <button
                type="button"
                className="wl-a"
                onClick={() => {
                  if (auto || locked || ck.kind === 'choice') return;
                  p.setDraft(r.id, { ticks: { ...(d.ticks ?? {}), [ck.key]: !tick(ck.key) } });
                }}
                disabled={p.writesOff}
                aria-pressed={on}
                style={sx('display:flex;align-items:flex-start;gap:10px;text-decoration:none;min-width:0;' + (auto || locked ? 'pointer-events:none;' : 'cursor:pointer;'))}
              >
                <span
                  style={sx(
                    'display:inline-flex;align-items:center;justify-content:center;width:17px;height:17px;flex:0 0 17px;border-radius:4px;font-size:14px;font-weight:700;color:#fff;border:1.5px solid ;white-space:nowrap;' +
                      (on ? '#1F6B52' : 'rgba(34,39,46,.28)') + ';background:' + (on ? '#1F6B52' : 'transparent') + ';',
                  )}
                >
                  {on ? '✓' : ''}
                </span>
                <span style={sx('font-size:16px;color:#3C454F;line-height:1.5;')}>{ck.label}</span>
              </button>
              <div style={sx(`padding-left:27px;font-size:14px;color:${on ? '#54606C' : '#3C454F'};`)}>{note}</div>
              {ck.kind === 'choice' && !locked && (
                <div style={sx('display:flex;gap:8px;padding-left:27px;margin-top:5px;')}>
                  {physBtn('attached', 'Photograph attached')}
                  {physBtn('na', 'Not applicable')}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {extra.length > 0 && (
        <>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-top:14px;')}>Also on the reporter checklist</div>
          <div data-testid="referral-checklist-extra" style={sx('display:grid;gap:7px;margin-top:8px;')}>
            {extra.map((ck) => {
              const on = tick(ck.key);
              return (
                <button
                  key={ck.key}
                  type="button"
                  className="wl-a"
                  aria-pressed={on}
                  disabled={p.writesOff}
                  onClick={() => { if (!locked) p.setDraft(r.id, { ticks: { ...(d.ticks ?? {}), [ck.key]: !on } }); }}
                  style={sx('display:flex;align-items:flex-start;gap:10px;text-decoration:none;min-width:0;' + (locked ? 'pointer-events:none;' : 'cursor:pointer;'))}
                >
                  <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:17px;height:17px;flex:0 0 17px;border-radius:4px;font-size:14px;font-weight:700;color:#fff;border:1.5px solid ' + (on ? '#1F6B52' : 'rgba(34,39,46,.28)') + ';background:' + (on ? '#1F6B52' : 'transparent') + ';')}>{on ? '✓' : ''}</span>
                  <span style={sx('font-size:16px;color:#3C454F;line-height:1.5;')}>{ck.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:4px;margin-top:12px;')}>
        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;color:#3C454F;background:rgba(34,39,46,.06);border-radius:4px;padding:2px 7px;white-space:nowrap;')}>{r.clockBasis ?? `${r.limitHours ?? 24} hours`}</span>
      </div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:6px;')}>{law}</div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:6px;')}>
        A good-faith report is confidential and protected from retaliation by statute. Notifying the administrator first is optional and is not a condition of reporting. Failure to report is a personal liability, not only the facility&rsquo;s.
      </div>
      {!locked && !p.ro && (
        <>
          <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;margin-top:14px;')}>
            <A
              onClick={() => ready && void p.file(r)}
              disabled={!ready || p.writesOff}
              title={p.offReason ?? undefined}
              style={
                'display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 15px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;border:1px solid ;white-space:nowrap;' +
                (ready ? '#1F6B52' : 'rgba(34,39,46,.14)') + ';color:' + (ready ? '#fff' : '#54606C') + ';background:' + (ready ? '#1F6B52' : 'rgba(34,39,46,.04)') + ';cursor:' + (ready ? 'pointer' : 'not-allowed') + ';'
              }
            >
              Record that I filed this report
            </A>
            {p.saveDraft && (
              <A
                onClick={() => void p.saveDraft!(r)}
                disabled={p.writesOff}
                title={p.offReason ?? undefined}
                style="display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;border:1px solid rgba(34,39,46,.18);color:#3C454F;background:#FCFAF4;white-space:nowrap;"
              >
                Save draft
              </A>
            )}
            <span style={sx('font-size:14px;color:#54606C;line-height:1.5;flex:1;min-width:180px;')}>{readyNote}</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:6px;')}>You would be recorded as {r.reporterName ?? p.me}.</div>
        </>
      )}
      {locked && (
        <div style={sx('border:1px solid rgba(47,138,111,.32);background:rgba(47,138,111,.07);border-radius:10px;padding:12px 14px;margin-top:14px;')}>
          <div style={sx('font-size:14px;font-weight:700;color:#1F6B52;')}>{`Filed by ${r.reporterName ?? 'the reporter'} · recorded ${recordedAt}`}</div>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;flex-wrap:wrap;margin-top:9px;')}>
            <span style={sx('font-size:14px;color:#3C454F;')}>{inScope ? 'Survey agency intake number' : `${c?.apsLabel ?? 'Agency'} case number`}</span>
            <input
              value={p.caseNo[r.id] ?? r.aps_reference ?? ''}
              onChange={(e) => p.setCaseNo(r.id, e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 14))}
              onBlur={(e) => e.target.value && e.target.value !== r.aps_reference && void p.saveCase(r, e.target.value)}
              placeholder="as given by intake"
              aria-label="Case number"
              readOnly={p.writesOff}
              style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#22272E;background:#fff;border:1px solid rgba(34,39,46,.16);border-radius:6px;padding:6px 9px;width:170px;outline:none;')}
            />
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:9px;')}>{readyNote}</div>
        </div>
      )}
    </div>
  );
}
